import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import TabBar from "../../tab-bar/TabBar";
import TabWithCount from "../../tab-with-count/TabWithCount";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import TabContainer from "../../tab-container/TabContainer";
import { useFormContext } from "react-hook-form";
import NoData from "../../no-data/NoData";
import { t } from "i18next";
import { Box, Button, Divider, Stack, useTheme } from "@mui/material";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import { isMobile, isTablet } from "react-device-detect";
import { Ref, forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { AlarmSetpointNotificationTabEnum, NotificationTypeEnum, UseAlarmSetpointNotificationStateType  } from "./AlarmSetpointNotificationState";
import SideSheetFilterInput from "../../side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetRadioGroup from "../../side-sheet/side-sheet-radio-group/SideSheetRadioGroup";
import SideSheetSwitch from "../../side-sheet/side-sheet-switch/SideSheetSwitch";
import SideSheet from "../../side-sheet/side-sheet/SideSheet";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import FormProperty from "../../forms/form-property/FormProperty";
import FormCheckbox from "../../forms/form-checkbox/FormCheckbox";
import { RecipientTypeEnum } from "../../../common/enums/RecipientTypeEnum";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useGroup } from "../../../queries/groups/UseGroup";
import OrganizationPicker from "../../organization-picker/OrganizationPicker";
import { IOrganization } from "../../../services/organization-data-service/Models";
import { useCustomerAttribute } from "../../../queries/attributes/UseCustomerAttribute";
import AlarmSetpointNotificationUtils, { radioButtons } from "./AlarmSetpointNotificationUtils";
import { ActionMenuItem, BaseActions } from "../../action-menu/ActionMenuItem";
import { FormRecipientActions, recipientActionItems } from "../../form-recipient-list/FormRecipientListUtils";
import FormRecipientList, { FormRecipientListType } from "../../form-recipient-list/FormRecipientList";
import FormNotificationLists, { FormNotificationListsType } from "../../form-notification-lists/FormNotificationLists";
import { FormNotificationListsActions, notificationListActionItems } from "../../form-notification-lists/FormNotificationListsUtils";
import AlarmSchemeList, { AlarmSchemeListType } from "../../alarm-scheme-list/AlarmSchemeList";
import { SetpointFormType } from "../AlarmSetpointEditorUtils";
import { AlarmSchemeListActionEnum, alarmSchemeActionItems } from "../../alarm-scheme-list/AlarmSchemeListUtils";

export type AlarmSetpointNotificationsType = {
    executeAction: (action: FormRecipientActions | FormNotificationListsActions | BaseActions) => void;
};


export type AlarmSetpointNotificationsProps = {
    groupID: number,
    openSideSheet?: boolean;
    onCloseSideSheet?: () => void;
    setViewOptionState?: (value: ViewOptionStateEnum) => void;
    setpointNotificationState: UseAlarmSetpointNotificationStateType
};

function AlarmSetpointNotifications({
    groupID,
    openSideSheet,
    onCloseSideSheet,
    setViewOptionState,
    setpointNotificationState
    
}: AlarmSetpointNotificationsProps, ref: Ref<unknown> | undefined) {

    const theme = useTheme();

    const [userState] = useUserContext();


    const recipientList = useRef<FormRecipientListType>(undefined);
    const notificationList = useRef<FormNotificationListsType>(undefined);
    const alarmSchemeList = useRef<AlarmSchemeListType>(undefined);

    useImperativeHandle<unknown, AlarmSetpointNotificationsType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );


    const {
        state, 
        setActiveTab,
        setViewSelected,
        setRecipientFilterText,
        setNotificationListFilterText,
        setAlarmSchemeFilterText,
        setNotificationType,
        setRecipientType,
        setOrganization,
        resetState

    } = setpointNotificationState;

    const {
        getValues,
        watch,
        trigger,
        //control,
        //reset,
        setValue,
        //resetField
    } = useFormContext<SetpointFormType>();    


    const {data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID
    });


    const {data: allowVoiceAlarmAttribute } = useQueryNotifications(useCustomerAttribute)({
        groupID ,
        name: 'allow voice alarm',
        enabled: group !== undefined
    });

    const allowVoiceAlarm = allowVoiceAlarmAttribute?.attributeValue.toLowerCase() === 'true';

    const [
            setPointID,
            organizationID,
            userRecipients, 
            notificationLists, 
            alarmSchemes,
            notifyOnAlarm
    ] = watch(['setPointID',
        'organizationID',
        'userRecipients', 
        'notificationLists', 
        'alarmSchemes',
        'notifyOnAlarm'
    ]);

    const isPhone = isMobile && !isTablet;

    const alarmScheme = alarmSchemes.find(o => o.selected);

    const recipientCount = userRecipients.length;
    const notificationListCount = notificationLists.length;

    const count = recipientCount + notificationListCount;

    const selectedRecipientCount = AlarmSetpointNotificationUtils.filterRecipients(userRecipients, 
                                        state.recipientFilterText, 
                                        state.recipientType, 
                                        state.organizationID === -1 ? undefined : state.organizationID)
                                        .flatMap(o => o.recipients)
                                        .filter(o => o.selected).length;
    //const selectedRecipientCount = userRecipients.flatMap(o => o.recipients).filter(o => o.selected).length;
    const selectedNotificationListCount = notificationLists.filter(o => o.selected).length;
    const selectedAlarmSchemeCount = alarmSchemes.filter(o => o.selected).length;

    const enableViewSelected = state.notificationType === NotificationTypeEnum.Tiered ? selectedAlarmSchemeCount > 0 :
                                state.activeTab === AlarmSetpointNotificationTabEnum.Recipients ?
                                    selectedRecipientCount > 0 :
                                    selectedNotificationListCount > 0;

    const objectType = state.notificationType === NotificationTypeEnum.Tiered ?
                        eLynxObjectTypeEnum.AlarmScheme :
                        state.activeTab === AlarmSetpointNotificationTabEnum.Recipients ?
                        eLynxObjectTypeEnum.Recipient :
                        eLynxObjectTypeEnum.NotificationList;
    



    function executeAction(action: FormRecipientActions | FormNotificationListsActions | AlarmSchemeListActionEnum | BaseActions) {

        if (state.notificationType === NotificationTypeEnum.Tiered) {
            alarmSchemeList.current?.executeAction(action as (AlarmSchemeListActionEnum | BaseActions));
        } else {
            switch(state.activeTab) {
                case AlarmSetpointNotificationTabEnum.Recipients: 
                    recipientList.current?.executeAction(action as (FormRecipientActions | BaseActions));
                    break;

                case AlarmSetpointNotificationTabEnum.NotificationLists: 
                    notificationList.current?.executeAction(action as (FormNotificationListsActions | BaseActions));
                    break;

                default:
                    break;
            }
        }
    }                        
    function onExecuteAction(menuItem: ActionMenuItem) {

        if (state.notificationType === NotificationTypeEnum.Tiered) {

            alarmSchemeList.current?.executeAction(menuItem.action as (AlarmSchemeListActionEnum | BaseActions));

        } else {

            switch(state.activeTab) {
                case AlarmSetpointNotificationTabEnum.Recipients: {
                    const action = menuItem.action as FormRecipientActions | BaseActions;
                    recipientList.current?.executeAction(action);
                }
                    break;

                case AlarmSetpointNotificationTabEnum.NotificationLists: {
                    const action = menuItem.action as FormNotificationListsActions | BaseActions;
                    notificationList.current?.executeAction(action);
                }
                    break;
            }
        }
    }

    function onViewSelected(value: boolean) {
        setViewSelected(value);
    }

    function onSetFilter(value: string) {

        objectType === eLynxObjectTypeEnum.Recipient ?
            setRecipientFilterText(value) :
        objectType === eLynxObjectTypeEnum.NotificationList ?
            setNotificationListFilterText(value) :
        setAlarmSchemeFilterText(value);
    }


    function onNotificationTypeChanged(value: NotificationTypeEnum) {
        setNotificationType(value);
    }

    function onRecipientTypeChanged(value: RecipientTypeEnum) {
        setRecipientType(value);
    }

    function onOrganizationChanged(item: IOrganization) {
        setOrganization(item.id);
    }

    function onNotifyChanged() {

        const notifyOnAlarm = getValues('notifyOnAlarm');

        if (!notifyOnAlarm) {
            setValue('notifyOnNormal', false);
        }

        trigger();
    }

    function onRecipientSelected(value: boolean) {

        if (value && alarmSchemes.find(o => o.selected)) {

            alarmSchemes.forEach((o, ndx) => {
                setValue(`alarmSchemes.${ndx}.selected`, false, {shouldDirty: true});
            })
        }
    }

    function onNotificationListSelected(value: boolean) {
        if (value && alarmSchemes.find(o => o.selected)) {

            alarmSchemes.forEach((o, ndx) => {
                setValue(`alarmSchemes.${ndx}.selected`, false, {shouldDirty: true});
            })
        }
    }

    function onAlarmSchemeSelected(index: number, value: boolean) {

        for (let i = 0; i < alarmSchemes.length; ++i) {
            setValue(`alarmSchemes.${i}.selected`, false, {shouldDirty: true});    
        }

        setValue(`alarmSchemes.${index}.selected`, value, {shouldDirty: true});

        if (value) {
            // an alarm scheme was selected, so clear any selected notification lists or recipients

            for (let i = 0; i < notificationLists.length; ++i) {
                setValue(`notificationLists.${i}.selected`, false, {shouldDirty: true});    
            }
    
            for (let i = 0; i < userRecipients.length; ++i) {

                const item = userRecipients[i];

                for (let x = 0; x < item.recipients.length; ++x) {
                    setValue(`userRecipients.${i}.recipients.${x}.selected`, false, {shouldDirty: true});
                }
            }
        }

        trigger('alarmSchemes');
    }

    useEffect(() => {

        if (alarmScheme !== undefined) {
            setNotificationType(NotificationTypeEnum.Tiered)
        }

    }, [setActiveTab,
        alarmScheme,
        setNotificationType
    ]);

    useEffect(() => {

        if (!state.organizationID && setPointID > 0 && state.organizationID !== organizationID) {
            setOrganization(organizationID);
        } else if (group && !state.organizationID && setPointID <= 0) {
            setOrganization(group.organizationID)
        }
    }, [
        group,
        state.organizationID,
        setOrganization,
        setPointID,
        organizationID
    ]);

    useEffect(() => {
        setViewOptionState && setViewOptionState(state.viewOptionState);
    },[
        state.viewOptionState,
        setViewOptionState
    ]);


    useEffect(() => {
        if (state.viewSelected) {


            if (state.notificationType === NotificationTypeEnum.Tiered) {
                if (selectedAlarmSchemeCount === 0) {
                    setViewSelected(false);
                }

            } else {
                
                if (state.activeTab === AlarmSetpointNotificationTabEnum.NotificationLists && selectedNotificationListCount === 0) {
                    setViewSelected(false);

                } else if (state.activeTab === AlarmSetpointNotificationTabEnum.Recipients && selectedRecipientCount === 0) {
                    setViewSelected(false);
                } 
            }
        }
    }, [
        state.notificationType,
        state.viewSelected,
        state.activeTab,
        setViewSelected,
        selectedRecipientCount,
        selectedNotificationListCount,
        selectedAlarmSchemeCount
    ]);

   return (count > 0 ? <Box
       sx={{
           display: 'flex',
           flexDirection: 'column',
           flex: '1 1 auto',
           overflow: 'hidden',
           position: 'relative'
       }}>

       <FormPropertySection
               label={t("setpointAdmin.notificationOptions")}
               sx={{
                   marginLeft: 1,
                   marginTop: !isPhone ? 1 : undefined,
                   marginBottom: 1
               }}
           >
           <FormProperty sx={{paddingBottom: 0}}>
               <FormCheckbox
                   name='notifyOnAlarm'
                   label={t('setpointAdmin.notifyOnAlarm')}
                   onChange={() => onNotifyChanged()}
                   sx={{ 
                       marginRight: 0,
                       marginTop: 0,
                       //textOverflow: "ellipsis",
                       //whiteSpace: "nowrap",
                       //overflow: "hidden",
                       '& > *': {
                           fontSize: '.9rem'
                       }
                   }} />

           </FormProperty>

           <FormProperty sx={{paddingBottom: 0}}>

           <FormCheckbox
                   name='notifyOnNormal'
                   label={t('setpointAdmin.notifyOnClear')}
                   onChange={() => onNotifyChanged()}
                   disabled={!notifyOnAlarm}
                   sx={{ 
                       marginRight: 0,
                       marginTop: 0,
                       //textOverflow: "ellipsis",
                       //whiteSpace: "nowrap",
                       //overflow: "hidden",
                       '& > *': {
                           fontSize: '.9rem'
                       }
                   }} />
           </FormProperty>

       </FormPropertySection>

       <Divider />

       {
       state.notificationType === NotificationTypeEnum.OneTime ?
       <TabBar value={state.activeTab}
           onChange={(_event, value) => setActiveTab(value)}>

           {recipientCount > 0 ? 
               <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.Recipient, true)} 
               count={recipientCount}
               //countDisabled={activeTab !== SelectColumnsStepTabEnum.DataValues}
               value={AlarmSetpointNotificationTabEnum.Recipients}
           /> : null
           }

           {notificationListCount > 0 ? 
           <TabWithCount label={getObjectTypeName(eLynxObjectTypeEnum.NotificationList, true)} 
               count={notificationListCount}
               //countDisabled={activeTab !== SelectColumnsStepTabEnum.Attributes}
               value={AlarmSetpointNotificationTabEnum.NotificationLists}
           /> : 
           null
           }

       </TabBar> : 
       null
       }

       {!isPhone ? 
           // NOTE this has to position absolute, othwerwise the Tab component throws errors
           <Box
               sx={{
                   position: 'absolute',
                   top: theme.spacing(1),
                   right: theme.spacing(3)
               }}>

               {(state.notificationType === NotificationTypeEnum.OneTime && 
                               state.activeTab === AlarmSetpointNotificationTabEnum.Recipients ? 
                               recipientActionItems : 
                               state.notificationType === NotificationTypeEnum.OneTime && 
                               state.activeTab === AlarmSetpointNotificationTabEnum.NotificationLists ?
                               notificationListActionItems :
                               alarmSchemeActionItems).map((o, ndx) => 
                                   <Button key={ndx} onClick={() => onExecuteAction(o)}>
                                       {o.label}
                                   </Button>


                               )
               }

           </Box> : 
           null 
       }

       <Stack
           direction='row'
           sx={{
               display: 'flex',
               flex: '1 1 auto',
               overflow: 'hidden'
           }}>        

           <TabContainer visible={state.activeTab === AlarmSetpointNotificationTabEnum.Recipients && state.notificationType === NotificationTypeEnum.OneTime}>
               <FormRecipientList visible={state.activeTab === AlarmSetpointNotificationTabEnum.Recipients}
                   ref={recipientList}
                   filterText={state.recipientFilterText}
                   recipientType={state.recipientType}
                   viewSelected={state.viewSelected}
                   organizationID={state.organizationID === -1 ? undefined : state.organizationID} 
                   allowVoiceAlarm={allowVoiceAlarm} 
                   onRecipientSelected={onRecipientSelected}/>
           </TabContainer>


           <TabContainer visible={state.activeTab === AlarmSetpointNotificationTabEnum.NotificationLists && state.notificationType === NotificationTypeEnum.OneTime}>
               <FormNotificationLists visible={state.activeTab === AlarmSetpointNotificationTabEnum.NotificationLists}
                       ref={notificationList}
                       groupID={groupID}
                       filterText={state.notificationListFilterText}
                       viewSelected={state.viewSelected}
                       organizationID={state.organizationID === -1 ? undefined : state.organizationID} 
                       onNotificationListSelected={onNotificationListSelected}
                       allowEdit={true}
                       />
           </TabContainer>


           {state.notificationType === NotificationTypeEnum.Tiered &&

               <TabContainer visible={true}>
                   <AlarmSchemeList
                       ref={alarmSchemeList}
                       groupID={groupID}
                       organizationID={organizationID}
                       viewSelected={state.viewSelected}
                       filterText={state.alarmSchemeFilterText}
                       onAlarmSchemeSelected={onAlarmSchemeSelected}
                       allowEdit={true}
                       enableSelection={true} />
               </TabContainer>
           
           }

           <SideSheet
               title={t("options")}
               open={openSideSheet ?? false}
               hideCloseButton={!isPhone}
               onClose={() => onCloseSideSheet && onCloseSideSheet()}
               onReset={resetState}
               >


               {
                   state.notificationType !== NotificationTypeEnum.Tiered && 
                   <OrganizationPicker 
                       label={getObjectTypeName(eLynxObjectTypeEnum.Organization)}
                       color="inherit"
                       //organizationID={state.organizationID ?? group?.organizationID as number} 
                       organizationID={state.organizationID ?? -1 } 
                       onSelected={onOrganizationChanged}
                       includeAllOption={true}
                       />
               }                    

               <SideSheetRadioGroup
                   label={t('setpointAdmin.notificationType')}
                   value={state.notificationType}
                   radioButtons={[{
                       label: t('setpointAdmin.oneTime'),
                       ariaLabel: "one time notification",
                       value: NotificationTypeEnum.OneTime
                   },{
                       label: t('setpointAdmin.tiered'),
                       ariaLabel: "tiered notification",
                       value: NotificationTypeEnum.Tiered
                   }]}
                   onChange={(event) => onNotificationTypeChanged(+event.target.value)}
               />

               <SideSheetSwitch
                   label={t("setpointAdmin.viewSelected", {name: getObjectTypeName(objectType, objectType === eLynxObjectTypeEnum.AlarmScheme ? false : true)})}
                   checked={state.viewSelected}
                   onChange={(checked: boolean) => {
                       onViewSelected(checked);
                   }}
                   disabled={!enableViewSelected} />

               <SideSheetFilterInput
                   label={t("setpointAdmin.filterByObject", {objectType: getObjectTypeName(objectType)})}
                   value={state.notificationType === NotificationTypeEnum.Tiered ? state.alarmSchemeFilterText :
                       state.activeTab === AlarmSetpointNotificationTabEnum.Recipients ?  state.recipientFilterText :
                       state.notificationListFilterText}
                   onChange={(value) => {
                       onSetFilter(value);
                   }} 
                   disabled={state.viewSelected}/>


               {state.notificationType === NotificationTypeEnum.OneTime && 
                   state.activeTab === AlarmSetpointNotificationTabEnum.Recipients &&

                   <SideSheetRadioGroup
                   label={t('setpointAdmin.recipientType')}
                   value={state.recipientType}
                   radioButtons={!allowVoiceAlarm ? radioButtons.filter(o => o.value !== RecipientTypeEnum.Voice) : radioButtons}
                   // radioButtons={[{
                   //     label: 'All',
                   //     ariaLabel: "all recipient types",
                   //     value: RecipientTypeEnum.Invalid
                   // },{
                   //     label: RecipientTypeEnum[RecipientTypeEnum.Email],
                   //     ariaLabel: "email",
                   //     value: RecipientTypeEnum.Email
                   // },{
                   //     label: RecipientTypeEnum[RecipientTypeEnum.Text],
                   //     ariaLabel: "text",
                   //     value: RecipientTypeEnum.Text
                   // },{
                   //     label: RecipientTypeEnum[RecipientTypeEnum.Voice],
                   //     ariaLabel: "voice",
                   //     value: RecipientTypeEnum.Voice
                   // }]}
                   onChange={(event) => onRecipientTypeChanged(+event.target.value)}
               />

               }


              

           </SideSheet>

       </Stack>

   </Box> : <Box sx={{
       padding: 1
   }}>
       <NoData text={t('setpointAdmin.nodata.noObjects')} />
   </Box>);
  }


  export default forwardRef(AlarmSetpointNotifications);